import React from 'react';
import { Loader } from '@mantine/core';
import { XMarkIcon } from '@heroicons/react/24/outline';
import InlineFeedback from './InlineFeedback';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  pending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  danger = false,
  pending = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="bg-widget-bg rounded-lg p-6 max-w-md w-full mx-4 border border-secondary shadow-xl">
        <div className="flex justify-between items-start mb-3">
          <h2 className="text-xl font-bold text-heading">{title}</h2>
          <button
            onClick={onCancel}
            disabled={pending}
            aria-label="Close"
            className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-body-color mb-6">{message}</p>
        {error && <InlineFeedback feedback={{ kind: 'error', message: error }} className="mb-4" />}
        <div className="flex gap-3">
          <button
            onClick={onConfirm}
            disabled={pending}
            aria-label={confirmLabel}
            className={`flex-1 ${danger ? 'btn-danger' : 'btn-primary-full'} text-sm flex items-center justify-center gap-2`}
          >
            {pending ? <Loader size="xs" color="white" /> : confirmLabel}
          </button>
          <button onClick={onCancel} disabled={pending} className="btn-secondary text-sm" autoFocus>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
